import { Helmet } from "react-helmet";
import { CheckCircle, Users, Shield, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import MuseVideoPlayer from "@/components/MuseVideoPlayer";

const DownsellAcceptance = () => {

  return (
    <>
      <Helmet>
        <title>Order Successful - WorkStreamer Suite | The Liberated Consultant</title>
        <meta name="description" content="Congratulations! Your WorkStreamer Suite order was successful. Access your tools and join our consultant community." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-book-lightBg">
        {/* Special Offer Bar */}
        <div className="bg-gradient-to-r from-book-yellow to-book-yellow text-book-darkBlue py-3 px-4 text-center">
          <p className="text-sm md:text-base font-semibold">
            ✅ ORDER COMPLETED - The Liberated Consultant
          </p>
        </div>

        {/* Progress Steps */}
        <div className="bg-gray-100 py-4 px-4">
          <div className="container mx-auto">
            <div className="flex items-center justify-center space-x-2 md:space-x-4 text-sm">
              <span className="text-gray-500">STEP 1: OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="text-gray-500">STEP 2: SPECIAL OFFER</span>
              <span className="text-gray-400">→</span>
              <span className="font-bold text-book-yellowDark">STEP 3: ACCESS</span>
            </div>
          </div>
        </div>

        {/* Hero Section */}
        <section className="bg-gradient-to-b from-book-yellow/10 to-book-blue/10 py-10">
          <div className="container mx-auto px-4 text-center">
            <h1 className="section-title text-book-darkBlue mb-8">
              Congratulations! Your Order was Successful
            </h1>
            
            {/* Video Section */}
            <div className="mb-8">
              <div className="relative bg-gray-900 rounded-lg overflow-hidden shadow-lg max-w-3xl mx-auto">
                <div className="aspect-video">
                  <MuseVideoPlayer videoId="tVfkpif" className="w-full h-full" />
                </div>
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <p className="text-lg text-book-darkBlue leading-relaxed">
                You've just made one of the smartest investments in your consulting career. By securing the 2 key WorkStreamer Accelerators – Project and Guide – you've positioned yourself to deliver exceptional client results while reclaiming 10+ hours per month.
              </p>
            </div>
          </div>
        </section>

        {/* What You've Accomplished */}
        <section className="bg-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-book-darkBlue mb-8">
                What You've Accomplished Today
              </h2>
              <div className="bg-book-yellow/10 rounded-lg p-8 mb-8">
                <p className="text-lg text-book-darkBlue leading-relaxed">
                  You didn't just purchase a book and software. You invested in consultant freedom. While other independent consultants struggle with endless screenshot marathons and excessive scope creep, you now have the professional-grade tools that transform how enterprise software consulting gets done.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Next Steps */}
        <section className="bg-book-darkBlue py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-12">
                Next Steps
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white/10 rounded-lg p-6 text-white">
                  <div className="bg-book-yellow rounded-full w-12 h-12 flex items-center justify-center text-book-darkBlue font-bold text-xl mb-4 mx-auto">
                    1
                  </div>
                  <p className="text-center">
                    Your book order has been submitted and will be shipped shortly.
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 text-white">
                  <div className="bg-book-yellow rounded-full w-12 h-12 flex items-center justify-center text-book-darkBlue font-bold text-xl mb-4 mx-auto">
                    2
                  </div>
                  <p className="text-center">
                    Check your email for details on how to login to WorkStreamer Guide
                  </p>
                </div>
                <div className="bg-white/10 rounded-lg p-6 text-white">
                  <div className="bg-book-yellow rounded-full w-12 h-12 flex items-center justify-center text-book-darkBlue font-bold text-xl mb-4 mx-auto">
                    3
                  </div>
                  <p className="text-center">
                    Access to WorkStreamer Project will be emailed separately shortly after signing up
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Community Section */}
        <section className="bg-gradient-to-b from-book-yellow/10 to-book-blue/10 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <Users size={60} className="text-book-blue" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-book-darkBlue mb-8">
                You're Not Going This Alone
              </h2>
              <div className="bg-white rounded-lg p-8 shadow-lg mb-8">
                <p className="text-lg text-book-darkBlue leading-relaxed mb-6">
                  Your purchase includes exclusive access to The Liberated Consultant Community – an online network where independent consultants share strategies, templates, and success stories. Inside, you'll connect with peers who understand the unique challenges of independent practice and celebrate wins together.
                </p>
                <Button 
                  size="lg" 
                  className="btn-primary"
                  onClick={() => window.open('https://liberatedconsultant.mn.co/share/NMnVsWS-fgFdON2V?utm_source=manual', '_blank')}
                >
                  Join The Community
                </Button>
                <p className="text-sm text-book-darkBlue/70 mt-4">
                  Look for the access button then enter your information and wait for approval (usually within a day or two).
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Guarantee Section */}
        <section className="bg-white py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <Shield size={60} className="text-book-red" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-book-darkBlue mb-8">
                The Confidence That Comes With Certainty
              </h2>
              <div className="bg-book-red/5 border border-book-red/20 rounded-lg p-8">
                <p className="text-lg text-book-darkBlue leading-relaxed mb-4">
                  You're protected by our <strong>365-day money-back guarantee</strong>. Deploy these accelerators across multiple client projects. If WorkStreamer doesn't deliver measurable time savings and improved client satisfaction, request a full refund – no questions asked.
                </p>
                <p className="text-xl font-semibold text-book-red">
                  This isn't about the software working. It's about you working smarter.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Client Feedback */}
        <section className="bg-book-darkBlue py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                What Clients Notice Immediately
              </h2>
              <p className="text-lg text-white/90 mb-12">
                Independent consultants using WorkStreamer report consistent feedback from clients:
              </p>
              <div className="grid md:grid-cols-3 gap-8">
                <div className="bg-white/10 rounded-lg p-6">
                  <div className="flex justify-center mb-4">
                    <Star size={24} className="text-book-yellow" />
                  </div>
                  <blockquote className="text-white italic">
                    "You're the most organized consultant we've worked with."
                  </blockquote>
                </div>
                <div className="bg-white/10 rounded-lg p-6">
                  <div className="flex justify-center mb-4">
                    <Star size={24} className="text-book-yellow" />
                  </div>
                  <blockquote className="text-white italic">
                    "You keep track of everything don't you?"
                  </blockquote>
                </div>
                <div className="bg-white/10 rounded-lg p-6">
                  <div className="flex justify-center mb-4">
                    <Star size={24} className="text-book-yellow" />
                  </div>
                  <blockquote className="text-white italic">
                "These training materials are actually useful – we can follow them."
                  </blockquote>
                </div>
              </div>
              <p className="text-lg text-white/90 mt-12">
                This reputation becomes your competitive advantage. When clients see professional execution backed by sophisticated tools, you command premium rates and generate referrals.
              </p>
            </div>
          </div>
        </section>

        {/* Coaching Call CTA */}
        <section className="bg-gradient-to-b from-book-yellow/10 to-book-blue/10 py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-book-darkBlue mb-8">
                Your Next Action
              </h2>
              <div className="bg-white rounded-lg p-8 shadow-lg">
                <p className="text-lg text-book-darkBlue mb-6">
                  Book your free coaching call with Rey. We will review where you're at in your journey going independent and what you'd like to accomplish.
                </p>
                <Button 
                  size="lg" 
                  className="btn-primary"
                  onClick={() => window.open('https://savvycal.com/workstreamer/coaching-call-with-rey', '_blank')}
                >
                  Book Your Free Coaching Call
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Welcome Section */}
        <section className="bg-book-darkBlue py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Welcome to the Liberation
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-8">
                You've joined the consultants who have forged a new path and taken control of their work life. 
                Your tools are ready. Your community is waiting. Now it's in your hands.
              </p>
              <div className="bg-white/10 rounded-lg p-6">
                <p className="text-white">
                  Questions? Email me at{" "}
                  <a 
                    href="mailto:rey.marques@workstreamer.com" 
                    className="text-book-yellow hover:text-book-yellow/80 transition-colors"
                  >
                    rey.marques@workstreamer.com
                  </a>
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default DownsellAcceptance;